import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
    MdClass, MdCheckCircle, MdSchedule, MdPerson, MdDashboard,
    MdNotifications, MdSearch, MdMenu, MdClose, MdAddCircle, MdLogout, MdTrendingUp, MdArrowForward, MdContentCopy,
    MdCurrencyRupee, MdPendingActions, MdVisibility, MdListAlt, MdAssignment, MdChevronLeft, MdChevronRight, MdError,
    MdFlipCameraIos, MdInventory, MdCameraAlt, MdHardware, MdRestore, MdLocationOn, MdLocalTaxi, MdFileDownloadDone
} from 'react-icons/md';
import { SiPhonepe, SiGooglepay } from 'react-icons/si';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../api/axios';
import logo from '../assets/logo.png';

import CenterInventoryView from '../components/CenterInventoryView';
import CenterInspectionForm from '../components/CenterInspectionForm';
import NoticeBoard from '../components/NoticeBoard';
import PenaltyCard from '../components/PenaltyCard';
import HardwareReportForm from '../components/HardwareReportForm';
import EngineerVisitStatus from '../components/EngineerVisitStatus';
import KushalYuvaForm from '../components/KushalYuvaForm';
import MyRegistrations from '../components/MyRegistrations';
import CenterAutoApproval from '../components/CenterAutoApproval'; // Added
import RemarksTicker from '../components/RemarksTicker'; // Added
import CenterFacilities from '../components/CenterFacilities'; // Added
import FreeAdmissionPage from '../components/FreeAdmissionPage'; // Added
import HalfAdmissionPage from '../components/HalfAdmissionPage'; // Added
import DrccApprovalPage from '../components/DrccApprovalPage'; // Added
import CenterTargetManagement from '../components/CenterTargetManagement';

export default function TeacherDashboard() {
    const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth > 900);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
            if (window.innerWidth > 900) setIsSidebarOpen(true);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    const [activeTab, setActiveTab] = useState('dashboard');
    const { user, logout } = useAuth();

    // Initial Load
    useEffect(() => {
        //    fetchData(); // If needed for refresh
    }, []);

    // Data State
    const [admissions, setAdmissions] = useState([]);
    const [stats, setStats] = useState({
        totalAdmissions: 0,
        pendingAdmissions: 0,
        totalRevenue: 0,
        todayEarning: 0,
        monthlyEarning: 0
    });
    const [loadingData, setLoadingData] = useState(true);

    // Filter, Search & Pagination State
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [filteredAdmissions, setFilteredAdmissions] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [teacherFilters, setTeacherFilters] = useState({ portalStatus: '', monthYear: '' });
    const itemsPerPage = 10;

    // Profile Modal
    const [showCenterProfile, setShowCenterProfile] = useState(false);
    const [showProfileMenu, setShowProfileMenu] = useState(false);

    // Student Details Modal
    const [selectedStudent, setSelectedStudent] = useState(null);

    // Lock body scroll when modal is open
    useEffect(() => {
        if (selectedStudent) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [selectedStudent]);

    // Form
    const [formData, setFormData] = useState({
        student_name: '', email: '', batch: '', batch_time: '', drcss_no: '', aadhar_no: '',
        course: '', admission_date: new Date().toISOString().split('T')[0], payment_amount: '1000', center_name: '', center_address: ''
    });

    const [loading, setLoading] = useState(false);
    const [msg, setMsg] = useState('');
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [successData, setSuccessData] = useState(null);
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [errorData, setErrorData] = useState(null);

    // Manual Payment State
    const [showManualPaymentModal, setShowManualPaymentModal] = useState(false);
    const [manualPaymentData, setManualPaymentData] = useState(null);
    const [utrNumber, setUtrNumber] = useState(''); // Added UTR State

    // Custom Confirm Modal (replaces window.confirm)
    const [confirmModal, setConfirmModal] = useState({ show: false, message: '', onYes: null });
    const showConfirmPopup = (message, onYes) => setConfirmModal({ show: true, message, onYes });
    const handleConfirmYes = () => { if (confirmModal.onYes) confirmModal.onYes(); setConfirmModal({ show: false, message: '', onYes: null }); };
    const handleConfirmNo = () => setConfirmModal({ show: false, message: '', onYes: null });

    // Admission Conducted By Confirmation
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [conductedBy, setConductedBy] = useState('');

    // --- Camera Logic ---
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const [isCameraOpen, setIsCameraOpen] = useState(false);
    const [cameraError, setCameraError] = useState(false);
    const [capturedImage, setCapturedImage] = useState(null);
    const [facingMode, setFacingMode] = useState('environment'); // Default to back camera

    const startCamera = async (mode = facingMode) => {
        setCameraError(false);
        // Stop any existing stream
        if (videoRef.current && videoRef.current.srcObject) {
            const tracks = videoRef.current.srcObject.getTracks();
            tracks.forEach(track => track.stop());
        }

        try {
            // Try requested mode
            const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: mode } });
            setIsCameraOpen(true);
            setTimeout(() => {
                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                    videoRef.current.play().catch(e => console.error(e));
                }
            }, 100);
        } catch (err) {
            console.error("Specific camera access error:", err);
            // If environment fails (e.g. laptop), try fallback to any video source
            if (mode === 'environment') {
                try {
                    const stream = await navigator.mediaDevices.getUserMedia({ video: true });
                    setIsCameraOpen(true);
                    setTimeout(() => {
                        if (videoRef.current) {
                            videoRef.current.srcObject = stream;
                            videoRef.current.play().catch(e => console.error(e));
                        }
                    }, 100);
                } catch (e) {
                    setCameraError(true);
                }
            } else {
                setCameraError(true);
            }
        }
    };

    const switchCamera = () => {
        const newMode = facingMode === 'environment' ? 'user' : 'environment';
        setFacingMode(newMode);
        startCamera(newMode);
    };

    const stopCamera = () => {
        setIsCameraOpen(false);
        if (videoRef.current && videoRef.current.srcObject) {
            const stream = videoRef.current.srcObject;
            const tracks = stream.getTracks();
            tracks.forEach(track => track.stop());
            videoRef.current.srcObject = null;
        }
    };

    const capturePhoto = () => {
        if (videoRef.current && canvasRef.current) {
            const context = canvasRef.current.getContext('2d');
            canvasRef.current.width = videoRef.current.videoWidth;
            canvasRef.current.height = videoRef.current.videoHeight;
            context.drawImage(videoRef.current, 0, 0, canvasRef.current.width, canvasRef.current.height);

            const dataUrl = canvasRef.current.toDataURL('image/jpeg');
            setCapturedImage(dataUrl);

            canvasRef.current.toBlob((blob) => {
                const file = new File([blob], `capture_${Date.now()}.jpg`, { type: "image/jpeg" });
                setFormData(prev => ({ ...prev, photo: file }));
            }, 'image/jpeg');

            stopCamera();
        }
    };

    const retakePhoto = () => {
        setCapturedImage(null);
        setFormData(prev => ({ ...prev, photo: null }));
        startCamera();
    };

    // Cleanup camera on unmount or tab switch
    useEffect(() => {
        return () => stopCamera();
    }, [activeTab]);

    // Theme
    const cardRadius = '8px';

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth > 900) setIsSidebarOpen(true);
            else setIsSidebarOpen(false);
        };
        window.addEventListener('resize', handleResize);

        // Fetch Data
        fetchDashboardData();

        // Check for Order ID
        const params = new URLSearchParams(window.location.search);
        const orderId = params.get('order_id');
        if (orderId) {
            verifyPayment(orderId);
        }

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    useEffect(() => {
        fetchDashboardData();
    }, [debouncedSearchTerm]);

    // Prefill Center Details
    useEffect(() => {
        if (user) {
            setFormData(prev => ({
                ...prev,
                center_name: user.center_name || '',
                center_address: user.center_address || ''
            }));
        }
    }, [user]);

    const fetchDashboardData = async () => {
        try {
            const searchParam = debouncedSearchTerm ? `?search=${debouncedSearchTerm}` : '';
            const res = await api.get(`/admissions${searchParam}`);
            
            const resData = res.data.admissions;
            const admissionData = Array.isArray(resData) ? resData : (resData?.data || []);
            setAdmissions(admissionData);

            const penRes = await api.get('/penalties/my');

            setStats({
                totalAdmissions: res.data.total_students || 0,
                pendingAdmissions: res.data.pending_admissions || 0,
                totalRevenue: res.data.total_revenue || 0,
                totalPenalties: penRes.data.total_amount || 0,
                todayEarning: res.data.today_earning || 0,
                monthlyEarning: res.data.monthly_earning || 0
            });

            // ⚠️ INCOMPLETE ADMISSION GUARD
            // If teacher has a pending admission with no UTR, force them to complete it
            if (res.data.incomplete_admission) {
                const inc = res.data.incomplete_admission;
                setManualPaymentData({
                    admission_id: inc.id,
                    student_name: inc.student_name,
                    amount: inc.payment_amount,
                    order_id: inc.order_id,
                    forced: true, // This flag disables Cancel button
                });
                setShowManualPaymentModal(true);
            }
        } catch (error) {
            console.error("Failed to fetch dashboard data");
        } finally {
            setLoadingData(false);
        }
    };


    // Handle Search & Filtering
    useEffect(() => {
        let result = admissions;

        // Search filter
        if (searchTerm) {
            const lowerTerm = searchTerm.toLowerCase();
            result = result.filter(adm =>
                adm.student_name.toLowerCase().includes(lowerTerm) ||
                adm.registration_no?.toLowerCase().includes(lowerTerm) ||
                adm.aadhar_no?.includes(lowerTerm)
            );
        }

        // Portal Status filter (admin_status: Confirm / Pending)
        if (teacherFilters.portalStatus) {
            result = result.filter(adm => adm.admin_status === teacherFilters.portalStatus);
        }

        // Month-Year filter (format: "M-YYYY")
        if (teacherFilters.monthYear) {
            const [fm, fy] = teacherFilters.monthYear.split('-');
            result = result.filter(adm => {
                const d = new Date(adm.admission_date || adm.created_at);
                return d.getMonth() + 1 === Number(fm) && d.getFullYear() === Number(fy);
            });
        }

        setFilteredAdmissions(result);
        setCurrentPage(1);
    }, [searchTerm, admissions, teacherFilters]);

    // Pagination Logic
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredAdmissions.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredAdmissions.length / itemsPerPage);

    const handleExport = () => {
        if (filteredAdmissions.length === 0) return alert('No data to export');

        const headers = ["Student Name", "Reg No", "Course", "Batch", "Amount", "Date", "Phone/Aadhar"];
        const rows = filteredAdmissions.map(adm => [
            adm.student_name,
            adm.registration_no || 'Pending',
            adm.course,
            adm.batch,
            adm.payment_amount,
            adm.admission_date,
            adm.aadhar_no || '-'
        ]);

        let csvContent = "data:text/csv;charset=utf-8,"
            + headers.join(",") + "\n"
            + rows.map(e => e.join(",")).join("\n");

        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", "admissions_data.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const verifyPayment = async (orderId) => {
        setLoading(true);
        // Clean URL only after we attempt verification to prevent "ghost" refresh issues
        const cleanUrl = () => {
            const url = new URL(window.location);
            url.searchParams.delete('order_id');
            url.searchParams.delete('order_status');
            window.history.replaceState({}, '', url);
        };

        try {
            const res = await api.post('/admissions/verify-payment', { order_id: orderId });
            if (res.data.status === 'success') {
                setSuccessData(res.data);
                setShowSuccessModal(true);
                setMsg('');
                fetchDashboardData(); // Refresh stats
                cleanUrl(); // Success: Clean URL
            } else {
                // If explicitly failed by backend (e.g. pending deleted), show error
                setMsg(res.data.message || 'Payment verification failed.');
                setErrorData({
                    message: 'Payment Failed or Pending',
                    details: res.data.message || 'The admission record has been removed because payment was not completed.'
                });
                setShowErrorModal(true);
                cleanUrl(); // Failure handled: Clean URL
            }
        } catch (error) {
            console.error(error);
            setMsg('Payment Verification Failed!');
            // Only clean URL if it's a 400/404/500 that we know is definitive
            // If it's a network error, we might want to keep it?
            // For now, let's clean it to avoid infinite loops if the backend is down, 
            // but the user can retry manually if needed.
            // Do NOT clean URL on error. Let the user refresh to retry.
            // cleanUrl();

            setErrorData({
                message: 'Processing Failed',
                details: error.response?.data?.message || error.message || 'Could not verify payment status'
            });
            setShowErrorModal(true);
        } finally {
            setLoading(false);
        }
    };

    const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);
    const closeSidebarMobile = () => {
        if (window.innerWidth <= 900) setIsSidebarOpen(false);
    }

    const hasPermission = (p) => {
        if (user?.role === 'admin') return true;

        // Strict RBAC for Faculty, LF, CC
        if (['faculty', 'lf', 'cc'].includes(user?.role)) {
            const perms = user?.permissions;

            // CRITICAL CHANGE: If permissions are NULL/Undefined, treat as NO ACCESS.
            if (!perms) return false;

            let parsed = perms;
            if (typeof perms === 'string') {
                try {
                    parsed = JSON.parse(perms);
                } catch (e) {
                    return false; // If parsing fails, NO ACCESS
                }
            }

            if (Array.isArray(parsed)) {
                return parsed.includes(p);
            }

            return false; // Valid array not found -> NO ACCESS
        }

        // For other roles (driver, engineer) or if not caught above, show by default
        return true;
    };


    // Fallback logic to switch from forbidden tabs
    useEffect(() => {
        if (user && ['faculty', 'lf', 'cc'].includes(user.role)) {
            if (!hasPermission(activeTab)) {
                const allTabs = ['dashboard', 'admission', 'my_admissions', 'free_admission', 'half_admission', 'drcc_approval', 'kyp_form', 'my_registrations', 'daily_progress', 'inspection', 'auto_approvals', 'hardware', 'inventory', 'center_facilities', 'notices'];
                const firstAllowed = allTabs.find(t => hasPermission(t));
                if (firstAllowed) setActiveTab(firstAllowed);
            }
        }
    }, [user, activeTab]);

    const handleInputChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handlePreSubmit = (e) => {
        e.preventDefault();
        setMsg('');
        // Directly process admission and assign system user name as conductor.
        handleFinalSubmit();
    };

    const handleFinalSubmit = async () => {
        setLoading(true);
        setMsg('');
        try {
            const payload = new FormData();
            Object.keys(formData).forEach(key => {
                payload.append(key, formData[key]);
            });
            // Auto setup conducted_by from the login session so user typing error doesn't break metrics
            payload.append('conducted_by', user?.name || 'Faculty');

            const res = await api.post('/admissions', payload, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            const data = res.data;

            if (data.manual_flow) {
                setManualPaymentData(data);
                setShowManualPaymentModal(true);
                setLoading(false);
            } else if (data.direct_success) {
                setSuccessData(data);
                setShowSuccessModal(true);
                setMsg('Admission Created Successfully!');
                setFormData(prev => ({
                    student_name: '', email: '', batch: '', batch_time: '', drcss_no: '', aadhar_no: '',
                    course: '', admission_date: '', payment_amount: '',
                    center_name: user?.center_name || '',
                    center_address: user?.center_address || ''
                }));
                setConductedBy(''); // Reset conducted by
                fetchDashboardData();
                setTimeout(() => window.location.reload(), 1500);
            } else if (data.payment_session_id) {
                const cashfree = window.Cashfree({ mode: "production" });
                cashfree.checkout({
                    paymentSessionId: data.payment_session_id,
                    returnUrl: `https://cmssecure.in/dashboard?order_id=${data.order_id}`
                });
            } else {
                setMsg('Failed to process admission.');
            }

        } catch (error) {
            console.error(error);
            if (error.response && error.response.data && error.response.data.message) {
                // Show actual server error
                setMsg(`Error: ${error.response.data.message}`);
                alert(`Server Error: ${error.response.data.message}\n${JSON.stringify(error.response.data.error || {}, null, 2)}`);
            } else {
                setMsg('Failed to process request. Check connection.');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ display: 'flex', height: '100vh', background: '#F0F2F5', fontFamily: 'var(--font-family-base)', overflow: 'hidden' }}>

            {isSidebarOpen && (
                <div
                    className="mobile-overlay"
                    onClick={() => setIsSidebarOpen(false)}
                    style={{
                        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                        background: 'rgba(0,0,0,0.5)', zIndex: 45, display: 'none'
                    }}
                />
            )}

            <motion.aside initial={false} animate={{ x: isSidebarOpen ? 0 : -280 }} transition={{ duration: 0.3 }} className="sidebar"
                style={{ background: '#1a2c3d', width: '260px', height: '100%', position: 'fixed', zIndex: 50, left: 0, top: 0, display: 'flex', flexDirection: 'column', borderRight: '1px solid #2d3748' }}>
                <div style={{ padding: '1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.1rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
                        <div style={{ background: 'white', padding: '1px', borderRadius: '5px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
                            <img src={logo} alt="Logo" style={{ height: '40px', width: '45px', objectFit: 'contain' }} />
                        </div>
                        <div>
                            <span style={{ display: 'block', fontWeight: '800', fontSize: '0.9rem', color: 'white', letterSpacing: '0.5px', lineHeight: '1.2' }}>SECURE CMS</span>
                            <span style={{ display: 'block', fontSize: '0.65rem', color: '#718096' }}>FACULTY PANEL</span>
                        </div>
                    </div>
                    <div className="mobile-only-close" onClick={() => setIsSidebarOpen(false)} style={{ color: 'white', cursor: 'pointer', display: 'none' }}><MdClose size={24} /></div>
                </div>
                <div style={{ height: '1px', background: 'rgba(255,255,255,0.1)', margin: '0 1rem' }}></div>

                <nav className="no-scrollbar" style={{ padding: '0.5rem 1rem', display: 'flex', flexDirection: 'column', gap: '0.1rem', flex: 1, overflowY: 'auto' }}>
                    {hasPermission('dashboard') && <NavItem icon={<MdDashboard />} label="Dashboard Overview" isActive={activeTab === 'dashboard'} onClick={() => { setActiveTab('dashboard'); closeSidebarMobile(); }} />}
                    {hasPermission('admission') && <NavItem icon={<MdAddCircle />} label="New Admission" isActive={activeTab === 'admission'} onClick={() => { setActiveTab('admission'); closeSidebarMobile(); }} />}
                    {hasPermission('my_admissions') && <NavItem icon={<MdListAlt />} label="My Admissions" isActive={activeTab === 'my_admissions'} onClick={() => { setActiveTab('my_admissions'); closeSidebarMobile(); }} />}
                    {hasPermission('free_admission') && <NavItem icon={<MdCurrencyRupee />} label="Free Admissions" isActive={activeTab === 'free_admission'} onClick={() => { setActiveTab('free_admission'); closeSidebarMobile(); }} badge="Free" />}
                    {hasPermission('half_admission') && <NavItem icon={<MdCurrencyRupee />} label="Half Admissions (500)" isActive={activeTab === 'half_admission'} onClick={() => { setActiveTab('half_admission'); closeSidebarMobile(); }} badge="Due" />}
                    {hasPermission('kyp_form') && <NavItem icon={<MdAssignment />} label="Registration Form" isActive={activeTab === 'kyp_form'} onClick={() => { setActiveTab('kyp_form'); closeSidebarMobile(); }} />}
                    {hasPermission('my_registrations') && <NavItem icon={<MdRestore />} label="My Registrations" isActive={activeTab === 'my_registrations'} onClick={() => { setActiveTab('my_registrations'); closeSidebarMobile(); }} />}
                    {hasPermission('drcc_approval') && <NavItem icon={<MdAssignment />} label="DRCC Approval" isActive={activeTab === 'drcc_approval'} onClick={() => { setActiveTab('drcc_approval'); closeSidebarMobile(); }} />}
                    {hasPermission('daily_progress') && <NavItem icon={<MdTrendingUp />} label="Admission Target" isActive={activeTab === 'daily_progress'} onClick={() => { setActiveTab('daily_progress'); closeSidebarMobile(); }} />}

                    {hasPermission('inspection') && <NavItem icon={<MdCameraAlt />} label="Daily Inspection" isActive={activeTab === 'inspection'} onClick={() => { setActiveTab('inspection'); closeSidebarMobile(); }} />}
                    {hasPermission('auto_approvals') && <NavItem icon={<MdLocalTaxi />} label="Auto Management" isActive={activeTab === 'auto_approvals'} onClick={() => { setActiveTab('auto_approvals'); closeSidebarMobile(); }} />}
                    {hasPermission('hardware') && <NavItem icon={<MdHardware />} label="Hardware Status" isActive={activeTab === 'hardware'} onClick={() => { setActiveTab('hardware'); closeSidebarMobile(); }} />}
                    {hasPermission('inventory') && <NavItem icon={<MdInventory />} label="My Assets" isActive={activeTab === 'inventory'} onClick={() => { setActiveTab('inventory'); closeSidebarMobile(); }} />}
                    {hasPermission('center_facilities') && <NavItem icon={<MdCheckCircle />} label="Center Assets" isActive={activeTab === 'center_facilities'} onClick={() => { setActiveTab('center_facilities'); closeSidebarMobile(); }} />}
                    {hasPermission('notices') && <NavItem icon={<MdNotifications />} label="Notice Board" isActive={activeTab === 'notices'} onClick={() => { setActiveTab('notices'); closeSidebarMobile(); }} />}
                </nav>

                <div style={{ padding: '1rem', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                    <button onClick={logout} style={{ width: '100%', padding: '0.8rem 1.2rem', background: '#e53e3e', color: 'white', border: 'none', borderRadius: cardRadius, fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.6rem', fontSize: '0.85rem', justifyContent: 'flex-start' }}>
                        <MdLogout /> Log Out
                    </button>
                </div>
            </motion.aside>

            <div className="main-content" style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden', marginLeft: (isSidebarOpen && window.innerWidth > 900) ? '260px' : '0px', transition: 'marginLeft 0.3s' }}>

                <header style={{ padding: '0.8rem 1rem', background: 'white', position: 'sticky', top: 0, zIndex: 30, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #E2E8F0', boxShadow: '0 2px 4px rgba(0,0,0,0.02)', flexShrink: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <div onClick={toggleSidebar} style={{ cursor: 'pointer', padding: '0.4rem' }}><MdMenu size={26} color="#4A5568" /></div>
                        <h2 style={{ fontSize: '1rem', fontWeight: '700', color: '#1A202C', margin: 0 }}>
                            Welcome, <span style={{ color: '#3182CE' }}>{user?.name?.split(' ')[0] || 'Faculty'}</span>
                        </h2>
                    </div>

                    {/* Top Right Profile with Click Trigger */}
                    <div style={{ position: 'relative' }}>
                        <div onClick={() => setShowProfileMenu(!showProfileMenu)} style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', cursor: 'pointer', padding: '4px 8px', borderRadius: '8px', transition: '0.2s' }} className="hover:bg-gray-100">
                            <div style={{ textAlign: 'right', display: window.innerWidth > 600 ? 'block' : 'none' }}>
                                <span style={{ display: 'block', fontSize: '0.85rem', fontWeight: '700', color: '#2D3748' }}>{user?.center_name || 'My Center'}</span>
                                <span style={{ display: 'block', fontSize: '0.7rem', color: '#718096' }}>{user?.name}</span>
                            </div>
                            <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'white', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', fontSize: '0.9rem', overflow: 'hidden', border: '2px solid #E2E8F0', boxShadow: '0 2px 5px rgba(0,0,0,0.1)' }}>
                                <img src={user?.center_logo_url || logo} alt="P" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                            </div>
                        </div>

                        <AnimatePresence>
                            {showProfileMenu && (
                                <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
                                    style={{ position: 'absolute', top: '120%', right: '0', width: '200px', background: 'white', borderRadius: '8px', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)', border: '1px solid #E2E8F0', zIndex: 100, overflow: 'hidden' }}
                                >
                                    <div style={{ padding: '1rem', borderBottom: '1px solid #E2E8F0', background: '#F7FAFC' }}>
                                        <div style={{ fontWeight: '700', color: '#2D3748', fontSize: '0.9rem' }}>{user?.center_name || 'Center'}</div>
                                        <div style={{ fontSize: '0.8rem', color: '#718096' }}>{user?.name}</div>
                                    </div>

                                    {/* DEBUG: Show Current Permissions */}
                                    {['faculty', 'lf', 'cc'].includes(user?.role) && (
                                        <div style={{ padding: '0.8rem 1rem', background: '#FFF7ED', borderBottom: '1px solid #FDBA74' }}>
                                            <div style={{ fontSize: '0.7rem', fontWeight: '700', color: '#9A3412', textTransform: 'uppercase', marginBottom: '0.3rem' }}>Access Level:</div>
                                            <div style={{ fontSize: '0.75rem', color: '#4A5568', lineHeight: '1.4' }}>
                                                {user?.permissions && Array.isArray(user.permissions) ? (
                                                    user.permissions.length > 0 ? (
                                                        <span style={{ color: '#16A34A', fontWeight: '600' }}>{user.permissions.length} menu(s) enabled</span>
                                                    ) : (
                                                        <span style={{ color: '#DC2626', fontWeight: '600' }}>No access granted</span>
                                                    )
                                                ) : (
                                                    <span style={{ color: '#EA580C', fontWeight: '600' }}>Full Access (Legacy)</span>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    <div onClick={() => { setShowCenterProfile(true); setShowProfileMenu(false); }} style={{ padding: '0.8rem 1rem', display: 'flex', alignItems: 'center', gap: '0.8rem', color: '#4A5568', cursor: 'pointer', transition: '0.2s' }} className="hover:bg-gray-100">
                                        <MdPerson size={18} /> My Profile
                                    </div>
                                    <div onClick={logout} style={{ padding: '0.8rem 1rem', display: 'flex', alignItems: 'center', gap: '0.8rem', color: '#E53E3E', cursor: 'pointer', borderTop: '1px solid #E2E8F0' }}>
                                        <MdLogout size={18} /> Sign Out
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </header>

                <main style={{ padding: isMobile ? '12px 16px' : '1rem', width: '100%', boxSizing: 'border-box', flex: 1, overflowY: 'auto', paddingBottom: '2rem', overscrollBehavior: 'contain' }}>
                    {activeTab === 'dashboard' && (
                        <div className="fade-in">
                            {/* Admin Remarks Ticker - Top Priority */}
                            {hasPermission('inspection') && <RemarksTicker />}

                            {/* Stats Grid */}
                            {hasPermission('inspection') && (
                                <div style={{ marginBottom: '2rem' }}>
                                    <PenaltyCard />
                                </div>
                            )}

                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(auto-fit, minmax(240px, 1fr))',
                                gap: isMobile ? '0.8rem' : '1.2rem',
                                marginBottom: '2rem'
                            }}>
                                {/* Total Admissions Card */}
                                <div style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', borderRadius: '12px', padding: isMobile ? '0.8rem' : '1.2rem', color: 'white', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', minHeight: isMobile ? '100px' : '130px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <div>
                                            <div style={{ fontSize: isMobile ? '0.75rem' : '0.9rem', fontWeight: '500', opacity: 0.9 }}>Confirmed Admissions</div>
                                            <div style={{ fontSize: isMobile ? '1.5rem' : '2.5rem', fontWeight: '700', marginTop: isMobile ? '0.2rem' : '0.5rem' }}>{stats.totalAdmissions}</div>
                                        </div>
                                        <div style={{ background: 'rgba(255,255,255,0.2)', padding: '0.4rem', borderRadius: '8px', display: isMobile ? 'none' : 'block' }}>
                                            <MdPerson size={20} color="white" />
                                        </div>
                                    </div>
                                    <div style={{ fontSize: isMobile ? '0.65rem' : '0.8rem', opacity: 0.8, marginTop: isMobile ? '0.5rem' : '1rem' }}>
                                        Verified Students
                                    </div>
                                </div>

                                {/* Pending Admissions Card */}
                                <div style={{ background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)', borderRadius: '12px', padding: isMobile ? '0.8rem' : '1.2rem', color: 'white', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', minHeight: isMobile ? '100px' : '130px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <div>
                                            <div style={{ fontSize: isMobile ? '0.75rem' : '0.9rem', fontWeight: '500', opacity: 0.9 }}>Total Registrations</div>
                                            <div style={{ fontSize: isMobile ? '1.5rem' : '2.5rem', fontWeight: '700', marginTop: isMobile ? '0.2rem' : '0.5rem' }}>{stats.pendingAdmissions}</div>
                                        </div>
                                        <div style={{ background: 'rgba(255,255,255,0.2)', padding: '0.4rem', borderRadius: '8px', display: isMobile ? 'none' : 'block' }}>
                                            <MdPendingActions size={20} color="white" />
                                        </div>
                                    </div>
                                    <div style={{ fontSize: isMobile ? '0.65rem' : '0.8rem', opacity: 0.8, marginTop: isMobile ? '0.5rem' : '1rem' }}>
                                        Success Rate 100%
                                    </div>
                                </div>

                                {/* Total Revenue Card */}
                                <div style={{ background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)', borderRadius: '12px', padding: isMobile ? '0.8rem' : '1.2rem', color: 'white', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', minHeight: isMobile ? '100px' : '130px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <div>
                                            <div style={{ fontSize: isMobile ? '0.75rem' : '0.9rem', fontWeight: '500', opacity: 0.9 }}>Total Revenue</div>
                                            <div style={{ fontSize: isMobile ? '1.5rem' : '2.5rem', fontWeight: '700', marginTop: isMobile ? '0.2rem' : '0.5rem' }}>₹{stats.totalRevenue.toLocaleString()}</div>
                                        </div>
                                        <div style={{ background: 'rgba(255,255,255,0.2)', padding: '0.4rem', borderRadius: '8px', display: isMobile ? 'none' : 'flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px' }}>
                                            <span style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>₹</span>
                                        </div>
                                    </div>
                                    <div style={{ fontSize: isMobile ? '0.65rem' : '0.8rem', opacity: 0.8, marginTop: isMobile ? '0.5rem' : '1rem' }}>
                                        Total Collection
                                    </div>
                                </div>

                                {/* Combined Earning Card */}
                                <div style={{ background: 'linear-gradient(135deg, #059669 0%, #10B981 100%)', borderRadius: '12px', padding: isMobile ? '0.8rem' : '1.2rem', color: 'white', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', minHeight: isMobile ? '100px' : '130px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <div>
                                            <div style={{ fontSize: isMobile ? '0.75rem' : '0.9rem', fontWeight: '500', opacity: 0.9 }}>Today's Earning</div>
                                            <div style={{ fontSize: isMobile ? '1.5rem' : '2.5rem', fontWeight: '700', marginTop: isMobile ? '0.2rem' : '0.5rem' }}>₹{stats.todayEarning.toLocaleString()}</div>
                                        </div>
                                        <div style={{ background: 'rgba(255,255,255,0.2)', padding: '0.4rem', borderRadius: '8px', display: isMobile ? 'none' : 'block' }}>
                                            <MdCurrencyRupee size={20} color="white" />
                                        </div>
                                    </div>
                                    <div style={{ borderTop: '1px solid rgba(255,255,255,0.2)', marginTop: isMobile ? '0.4rem' : '0.5rem', paddingTop: isMobile ? '0.4rem' : '0.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <div style={{ fontSize: isMobile ? '0.65rem' : '0.75rem', opacity: 0.9 }}>
                                            Monthly: <span style={{ fontWeight: '700' }}>₹{stats.monthlyEarning.toLocaleString()}</span>
                                        </div>
                                        <MdTrendingUp size={isMobile ? 12 : 14} style={{ opacity: 0.8 }} />
                                    </div>
                                </div>
                            </div>

                            {/* Dashboard Content Grid */}
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(290px, 1fr))', gap: '2rem' }}>

                                {/* Recent Admissions List */}
                                <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #E2E8F0', padding: '1.5rem', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                                        <h3 style={{ fontSize: '1.1rem', fontWeight: '700', color: '#2D3748', margin: 0 }}>Recent Admissions</h3>
                                        <button onClick={() => setActiveTab('my_admissions')} style={{ color: '#319795', background: 'transparent', border: 'none', fontWeight: '600', cursor: 'pointer', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                            View All <MdArrowForward />
                                        </button>
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                        {admissions.length === 0 ? (
                                            <div style={{ textAlign: 'center', color: '#A0AEC0', padding: '2rem' }}>No admissions yet.</div>
                                        ) : (
                                            admissions.slice(0, 5).map((student, index) => (
                                                <div key={student.id || index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '1rem', borderBottom: index < 4 ? '1px solid #EDF2F7' : 'none' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                                        <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: '#E2E8F0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', color: '#4A5568' }}>
                                                            {student.student_name.charAt(0)}
                                                        </div>
                                                        <div>
                                                            <div style={{ fontWeight: '700', color: '#2D3748', fontSize: '0.95rem' }}>{student.student_name}</div>
                                                            <div style={{ fontSize: '0.8rem', color: '#718096' }}>{student.course} • {new Date(student.created_at).toLocaleDateString()}</div>
                                                        </div>
                                                    </div>
                                                    <div style={{ textAlign: 'right' }}>
                                                        {student.payment_status === 'rejected' ? (
                                                            <div style={{ fontSize: '0.8rem', color: '#E53E3E', fontWeight: '600' }}>Rejected</div>
                                                        ) : student.registration_no ? (
                                                            <div style={{ fontSize: '0.85rem', color: '#4A5568', fontWeight: '500' }}>{student.registration_no}</div>
                                                        ) : (
                                                            <div style={{ fontSize: '0.8rem', color: '#DD6B20', fontWeight: '600' }}>Processing</div>
                                                        )}
                                                        {student.payment_status === 'rejected' ? (
                                                            <span style={{ padding: '2px 8px', background: '#FED7D7', color: '#9B2C2C', borderRadius: '10px', fontSize: '0.7rem', fontWeight: 'bold', marginTop: '4px', display: 'inline-block' }}>Rejected</span>
                                                        ) : student.payment_status === 'paid' ? (
                                                            <span style={{ padding: '2px 8px', background: '#C6F6D5', color: '#22543D', borderRadius: '10px', fontSize: '0.7rem', fontWeight: 'bold', marginTop: '4px', display: 'inline-block' }}>Paid</span>
                                                        ) : (
                                                            <span style={{ padding: '2px 8px', background: '#FEEBC8', color: '#C05621', borderRadius: '10px', fontSize: '0.7rem', fontWeight: 'bold', marginTop: '4px', display: 'inline-block' }}>Pending</span>
                                                        )}
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                    <div style={{ marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid #E2E8F0' }}>
                                        <button onClick={() => setActiveTab('admission')} style={{ width: '100%', padding: '0.8rem', background: '#2B6CB0', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                                            <MdAddCircle size={20} /> Create New Admission
                                        </button>
                                    </div>
                                </div>

                                {/* Monthly Chart */}
                                <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #E2E8F0', padding: '1.5rem', boxShadow: '0 2px 4px rgba(0,0,0,0.02)', display: 'flex', flexDirection: 'column' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                                        <h3 style={{ fontSize: '1.1rem', fontWeight: '700', color: '#2D3748', margin: 0 }}>Monthly Performance</h3>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', color: '#718096' }}>
                                            <MdTrendingUp color="#38A169" /> Admissions & Revenue
                                        </div>
                                    </div>

                                    <div style={{ flex: 1, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', paddingLeft: '10px', height: '200px' }}>
                                        {/* Mock Data for Visualization */}
                                        {[45, 60, 35, 70, 50, stats.totalAdmissions > 0 ? 80 : 30].map((h, i) => (
                                            <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem', width: '100%' }}>
                                                <div style={{
                                                    width: '60%',
                                                    height: `${h * 1.5}px`,
                                                    background: i === 5 ? '#805AD5' : '#E2E8F0',
                                                    borderRadius: '6px 6px 0 0',
                                                    transition: 'height 1s ease-out'
                                                }}></div>
                                                <div style={{ fontSize: '0.75rem', color: '#718096', fontWeight: '600' }}>{['Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan'][i]}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                            </div>
                        </div>
                    )}

                    {activeTab === 'my_admissions' && (
                        <div className="fade-in">
                            {/* ── Header ── Desktop: 1 row | Mobile: 2 rows ── */}
                            <div style={{
                                background: 'linear-gradient(90deg, #3182CE 0%, #2B6CB0 100%)',
                                padding: '0.6rem 0.75rem',
                                borderRadius: isMobile ? '0' : '12px 12px 0 0',
                                boxShadow: '0 4px 6px rgba(0,0,0,0.05)',
                            }}>

                                {isMobile ? (
                                    /* ══ MOBILE: 2 Clean Rows ══ */
                                    <>
                                        {/* Row 1: Title (left) + Search (right) */}
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', flexShrink: 0 }}>
                                                <MdListAlt size={18} style={{ color: 'white' }} />
                                                <h2 style={{ fontSize: '0.82rem', fontWeight: '700', color: 'white', margin: 0, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                                                    My Admissions
                                                </h2>
                                            </div>
                                            <div style={{ position: 'relative', width: '52%' }}>
                                                <MdSearch style={{ position: 'absolute', left: '7px', top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.7)' }} size={14} />
                                                <input
                                                    type="text" placeholder="Search name, reg..."
                                                    value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                                                    style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', color: 'white', padding: '0.35rem 0.4rem 0.35rem 1.7rem', borderRadius: '4px', fontSize: '0.78rem', width: '100%', outline: 'none', boxSizing: 'border-box' }}
                                                    className="placeholder-white"
                                                />
                                            </div>
                                        </div>

                                        {/* Row 2: Status filter (left) + Month filter (right) + Reset */}
                                        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '0.4rem' }}>
                                            {/* Status */}
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', background: 'rgba(255,255,255,0.12)', borderRadius: '4px', padding: '0 0.45rem', border: '1px solid rgba(255,255,255,0.22)', flex: 1 }}>
                                                <MdCheckCircle size={13} color={teacherFilters.portalStatus === 'Confirm' ? '#68D391' : teacherFilters.portalStatus === 'Pending' ? '#F6AD55' : 'rgba(255,255,255,0.5)'} />
                                                <select value={teacherFilters.portalStatus}
                                                    onChange={e => { setTeacherFilters(f => ({ ...f, portalStatus: e.target.value })); setCurrentPage(1); }}
                                                    style={{ background: 'transparent', border: 'none', color: 'white', padding: '0.32rem 0', fontSize: '0.78rem', outline: 'none', cursor: 'pointer', fontWeight: '600', width: '100%' }}>
                                                    <option value="" style={{ color: 'black', background: 'white' }}>All Status</option>
                                                    <option value="Confirm" style={{ color: 'black', background: 'white' }}>Confirm</option>
                                                    <option value="Pending" style={{ color: 'black', background: 'white' }}>Pending</option>
                                                </select>
                                            </div>
                                            {/* Month */}
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', background: 'rgba(255,255,255,0.12)', borderRadius: '4px', padding: '0 0.45rem', border: '1px solid rgba(255,255,255,0.22)', flex: 1 }}>
                                                <MdSchedule size={13} color="rgba(255,255,255,0.6)" />
                                                <select value={teacherFilters.monthYear}
                                                    onChange={e => { setTeacherFilters(f => ({ ...f, monthYear: e.target.value })); setCurrentPage(1); }}
                                                    style={{ background: 'transparent', border: 'none', color: 'white', padding: '0.32rem 0', fontSize: '0.78rem', outline: 'none', cursor: 'pointer', fontWeight: '600', width: '100%' }}>
                                                    <option value="" style={{ color: 'black', background: 'white' }}>All Months</option>
                                                    {(() => {
                                                        const MN = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                                                        const opts = []; const now = new Date();
                                                        for (let i = 0; i < 18; i++) {
                                                            const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
                                                            const m = d.getMonth() + 1; const y = d.getFullYear();
                                                            opts.push(<option key={`${m}-${y}`} value={`${m}-${y}`} style={{ color: 'black', background: 'white' }}>{MN[m]} {y}</option>);
                                                        }
                                                        return opts;
                                                    })()}
                                                </select>
                                            </div>
                                            {/* Reset — only when active */}
                                            {(teacherFilters.portalStatus || teacherFilters.monthYear || searchTerm) && (
                                                <button onClick={() => { setTeacherFilters({ portalStatus: '', monthYear: '' }); setSearchTerm(''); }}
                                                    style={{ display: 'flex', alignItems: 'center', gap: '0.2rem', background: 'rgba(220,53,69,0.4)', border: '1px solid rgba(255,150,150,0.4)', color: 'white', padding: '0.32rem 0.55rem', borderRadius: '4px', cursor: 'pointer', fontSize: '0.76rem', fontWeight: '700', flexShrink: 0 }}>
                                                    <MdClose size={13} /> Reset
                                                </button>
                                            )}
                                        </div>
                                    </>
                                ) : (
                                    /* ══ DESKTOP: 1 Row ══ */
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                                        {/* Title */}
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexShrink: 0 }}>
                                            <MdListAlt size={20} style={{ color: 'white' }} />
                                            <h2 style={{ fontSize: '0.95rem', fontWeight: '700', color: 'white', margin: 0, textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>
                                                My Admissions Directory
                                            </h2>
                                        </div>
                                        {/* Right-side controls */}
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginLeft: 'auto' }}>
                                            {/* Search */}
                                            <div style={{ position: 'relative', width: '200px' }}>
                                                <MdSearch style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.7)' }} size={15} />
                                                <input type="text" placeholder="Search name, reg..."
                                                    value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                                                    style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', color: 'white', padding: '0.38rem 0.5rem 0.38rem 1.8rem', borderRadius: '4px', fontSize: '0.82rem', width: '100%', outline: 'none', boxSizing: 'border-box' }}
                                                    className="placeholder-white" />
                                            </div>
                                            {/* Status */}
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', background: 'rgba(255,255,255,0.12)', borderRadius: '4px', padding: '0 0.5rem', border: '1px solid rgba(255,255,255,0.25)' }}>
                                                <MdCheckCircle size={14} color={teacherFilters.portalStatus === 'Confirm' ? '#68D391' : teacherFilters.portalStatus === 'Pending' ? '#F6AD55' : 'rgba(255,255,255,0.55)'} />
                                                <select value={teacherFilters.portalStatus}
                                                    onChange={e => { setTeacherFilters(f => ({ ...f, portalStatus: e.target.value })); setCurrentPage(1); }}
                                                    style={{ background: 'transparent', border: 'none', color: 'white', padding: '0.35rem 0.1rem', fontSize: '0.82rem', outline: 'none', cursor: 'pointer', fontWeight: '600', minWidth: '100px' }}>
                                                    <option value="" style={{ color: 'black', background: 'white' }}>All Status</option>
                                                    <option value="Confirm" style={{ color: 'black', background: 'white' }}>Confirm</option>
                                                    <option value="Pending" style={{ color: 'black', background: 'white' }}>Pending</option>
                                                </select>
                                            </div>
                                            {/* Month */}
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', background: 'rgba(255,255,255,0.12)', borderRadius: '4px', padding: '0 0.5rem', border: '1px solid rgba(255,255,255,0.25)' }}>
                                                <MdSchedule size={14} color="rgba(255,255,255,0.65)" />
                                                <select value={teacherFilters.monthYear}
                                                    onChange={e => { setTeacherFilters(f => ({ ...f, monthYear: e.target.value })); setCurrentPage(1); }}
                                                    style={{ background: 'transparent', border: 'none', color: 'white', padding: '0.35rem 0.1rem', fontSize: '0.82rem', outline: 'none', cursor: 'pointer', fontWeight: '600', minWidth: '135px' }}>
                                                    <option value="" style={{ color: 'black', background: 'white' }}>All Months</option>
                                                    {(() => {
                                                        const MN = ['', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
                                                        const opts = []; const now = new Date();
                                                        for (let i = 0; i < 18; i++) {
                                                            const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
                                                            const m = d.getMonth() + 1; const y = d.getFullYear();
                                                            opts.push(<option key={`${m}-${y}`} value={`${m}-${y}`} style={{ color: 'black', background: 'white' }}>{MN[m]} {y}</option>);
                                                        }
                                                        return opts;
                                                    })()}
                                                </select>
                                            </div>
                                            {/* Reset */}
                                            {(teacherFilters.portalStatus || teacherFilters.monthYear || searchTerm) && (
                                                <button onClick={() => { setTeacherFilters({ portalStatus: '', monthYear: '' }); setSearchTerm(''); }}
                                                    style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', background: 'rgba(220,53,69,0.4)', border: '1px solid rgba(255,150,150,0.45)', color: 'white', padding: '0.35rem 0.7rem', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '700' }}>
                                                    <MdClose size={14} /> Reset
                                                </button>
                                            )}
                                            {/* Export */}
                                            <button onClick={handleExport} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', background: 'white', border: 'none', color: '#2B6CB0', padding: '0.38rem 0.85rem', borderRadius: '4px', cursor: 'pointer', fontWeight: '700', fontSize: '0.82rem' }}>
                                                <MdFileDownloadDone size={16} /> Export
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Table Container */}
                            <div style={{ background: 'white', borderRadius: '0 0 12px 12px', border: '1px solid #E2E8F0', borderTop: 'none', overflow: 'hidden', boxShadow: '0 4px 6px rgba(0,0,0,0.02)' }}>
                                <div style={{ overflowX: 'auto' }} className="no-scrollbar">
                                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: isMobile ? '1200px' : '100%', whiteSpace: isMobile ? 'nowrap' : 'normal' }}>
                                        <thead style={{ background: '#F8FAFC', borderBottom: '1px solid #E2E8F0' }}>
                                            <tr>
                                                <Th>Student</Th>
                                                <Th>Reg No</Th>
                                                <Th>Course</Th>
                                                <Th>Batch</Th>
                                                <Th>Fees Paid</Th>
                                                <Th>Admission Date</Th>
                                                <Th>Portal Status</Th>
                                                <Th>Action</Th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <AnimatePresence mode="popLayout">
                                                {currentItems.length > 0 ? (
                                                    currentItems.map(adm => (
                                                        <motion.tr
                                                            key={adm.id}
                                                            layout
                                                            initial={{ opacity: 0 }}
                                                            animate={{ opacity: 1 }}
                                                            exit={{ opacity: 0 }}
                                                            onClick={() => setSelectedStudent(adm)}
                                                            style={{
                                                                borderBottom: '1px solid #EDF2F7',
                                                                cursor: 'pointer',
                                                                background: adm.admin_status === 'Confirm' ? '#F0FFF4' : 'white',
                                                                transition: 'background 0.2s'
                                                            }}
                                                        >
                                                            <Td>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
                                                                    <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: '#EDF2F7', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', color: '#718096', border: '1px solid #E2E8F0' }}>
                                                                        {adm.photo_path ? (
                                                                            <img src={adm.photo_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                                        ) : adm.student_name.charAt(0)}
                                                                    </div>
                                                                    <div>
                                                                        <div style={{ fontWeight: '600', color: adm.admin_status === 'Confirm' ? '#276749' : '#2D3748', fontSize: '0.9rem' }}>{adm.student_name}</div>
                                                                        <div style={{ fontSize: '0.75rem', color: '#718096' }}>{adm.aadhar_no}</div>
                                                                    </div>
                                                                </div>
                                                            </Td>
                                                            <Td>
                                                                {adm.payment_status === 'rejected' ? (
                                                                    <span style={{ padding: '2px 8px', background: '#FED7D7', color: '#9B2C2C', borderRadius: '4px', fontSize: '0.75rem', fontWeight: '600' }}>Rejected</span>
                                                                ) : adm.registration_no ? (
                                                                    <span style={{ fontWeight: '600', color: '#2D3748' }}>{adm.registration_no}</span>
                                                                ) : (
                                                                    <span style={{ padding: '2px 8px', background: '#FEFCBF', color: '#B7791F', borderRadius: '4px', fontSize: '0.75rem', fontWeight: '600' }}>Processing</span>
                                                                )}
                                                            </Td>
                                                            <Td>
                                                                <div style={{ fontWeight: '600', color: '#4A5568', fontSize: '0.85rem' }}>{adm.course}</div>
                                                            </Td>
                                                            <Td>
                                                                <div style={{ fontSize: '0.85rem', color: '#4A5568' }}>{adm.batch}</div>
                                                            </Td>
                                                            <Td>
                                                                <span style={{ fontWeight: '700', color: '#38A169' }}>₹{adm.payment_amount}</span>
                                                            </Td>
                                                            <Td>{new Date(adm.admission_date).toLocaleDateString()}</Td>
                                                            <Td onClick={e => e.stopPropagation()}>
                                                                {adm.admin_status === 'Confirm' ? (
                                                                    <span style={{
                                                                        display: 'inline-block',
                                                                        padding: '4px 10px', borderRadius: '4px',
                                                                        background: '#C6F6D5', color: '#276749',
                                                                        fontSize: '0.78rem', fontWeight: '700',
                                                                        border: '1px solid #9AE6B4'
                                                                    }}>✓ Confirm</span>
                                                                ) : (
                                                                    <select
                                                                        defaultValue={adm.admin_status || 'Pending'}
                                                                        onChange={async (e) => {
                                                                            const newStatus = e.target.value;
                                                                            const sel = e.target;
                                                                            if (newStatus === 'Confirm') {
                                                                                showConfirmPopup(
                                                                                    'Kya yeh admission Bihar Govt ke portal par ho gaya hai?',
                                                                                    async () => {
                                                                                        try {
                                                                                            await api.put(`/admissions/${adm.id}/admin-status`, { admin_status: newStatus });
                                                                                            adm.admin_status = newStatus;
                                                                                            fetchDashboardData();
                                                                                        } catch (err) {
                                                                                            alert('Status update failed: ' + (err.response?.data?.message || err.message));
                                                                                            sel.value = 'Pending';
                                                                                        }
                                                                                    }
                                                                                );
                                                                                sel.value = 'Pending'; // reset visually until confirmed
                                                                            }
                                                                        }}
                                                                        style={{
                                                                            padding: '4px 6px', borderRadius: '4px',
                                                                            border: '1px solid #CBD5E0',
                                                                            fontSize: '0.8rem', background: '#F7FAFC',
                                                                            cursor: 'pointer', color: '#4A5568'
                                                                        }}
                                                                    >
                                                                        <option value="Pending">Pending</option>
                                                                        <option value="Confirm">Confirm</option>
                                                                    </select>
                                                                )}
                                                            </Td>
                                                            <Td>
                                                                <button style={{ background: 'transparent', border: '1px solid #CBD5E0', padding: '4px 10px', borderRadius: '4px', cursor: 'pointer', color: '#4A5568', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.8rem' }}>
                                                                    <MdVisibility /> Details
                                                                </button>
                                                            </Td>
                                                        </motion.tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="6" style={{ padding: '3rem', textAlign: 'center', color: '#A0AEC0' }}>
                                                            {searchTerm ? 'No matching students found.' : 'No admissions found.'}
                                                        </td>
                                                    </tr>
                                                )}
                                            </AnimatePresence>
                                        </tbody>
                                    </table>
                                </div>

                                {/* Pagination Controls */}
                                {totalPages > 1 && (
                                    <div style={{ padding: '1rem', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '0.5rem', borderTop: '1px solid #E2E8F0', background: '#F8FAFC' }}>
                                        <button
                                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                            disabled={currentPage === 1}
                                            style={{ padding: '0.4rem 0.8rem', border: '1px solid #CBD5E0', borderRadius: '4px', background: currentPage === 1 ? '#EDF2F7' : 'white', cursor: currentPage === 1 ? 'not-allowed' : 'pointer', color: '#4A5568' }}
                                        >
                                            <MdChevronLeft />
                                        </button>
                                        <span style={{ fontSize: '0.85rem', color: '#4A5568', padding: '0 0.5rem', fontWeight: '600' }}>Page {currentPage} of {totalPages}</span>
                                        <button
                                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                            disabled={currentPage === totalPages}
                                            style={{ padding: '0.4rem 0.8rem', border: '1px solid #CBD5E0', borderRadius: '4px', background: currentPage === totalPages ? '#EDF2F7' : 'white', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', color: '#4A5568' }}
                                        >
                                            <MdChevronRight />
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}



                    {activeTab === 'free_admission' && (
                        <div className="fade-in">
                            <FreeAdmissionPage />
                        </div>
                    )}

                    {activeTab === 'half_admission' && (
                        <div className="fade-in">
                            <HalfAdmissionPage />
                        </div>
                    )}

                    {activeTab === 'drcc_approval' && (
                        <div className="fade-in">
                            <DrccApprovalPage />
                        </div>
                    )}

                    {activeTab === 'inspection' && (
                        <div className="fade-in">
                            <CenterInspectionForm />
                        </div>
                    )}

                    {activeTab === 'hardware' && (
                        <div className="fade-in">
                            <HardwareReportForm />
                        </div>
                    )}

                    {activeTab === 'inventory' && (
                        <div className="fade-in">
                            <CenterInventoryView user={user} />
                        </div>
                    )}

                    {activeTab === 'notices' && (
                        <div className="fade-in">
                            <NoticeBoard />
                        </div>
                    )}

                    {activeTab === 'kyp_form' && (
                        <div className="fade-in">
                            <KushalYuvaForm />
                        </div>
                    )}

                    {activeTab === 'my_registrations' && (
                        <div className="fade-in">
                            <MyRegistrations />
                        </div>
                    )}

                    {activeTab === 'auto_approvals' && (
                        <div className="fade-in">
                            <CenterAutoApproval />
                        </div>
                    )}

                    {activeTab === 'admission' && (
                        <motion.div
                            key="admission-form"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.5 }}
                            style={{
                                background: 'white',
                                borderRadius: cardRadius,
                                border: '1px solid #E2E8F0',
                                boxShadow: '0 4px 6px rgba(0,0,0,0.02)',
                                marginBottom: '2rem'
                            }}
                        >
                            {/* Styled Header Strip */}
                            <div style={{
                                background: 'linear-gradient(90deg, #3182CE 0%, #0056b3 100%)',
                                padding: '1.5rem',
                                color: 'white'
                            }}>
                                <h2 style={{ fontSize: '1.3rem', fontWeight: '800', margin: '0 0 0.3rem 0', letterSpacing: '0.5px' }}>ADMISSION FORM</h2>
                                <p style={{ fontSize: '0.9rem', opacity: 0.9, margin: 0 }}>Fill in the student details below to generate a new admission record.</p>
                            </div>

                            <div style={{ padding: isMobile ? '1rem' : '2rem', paddingBottom: isMobile ? '2rem' : '2rem' }}>
                                {/* Only show msg if it is NOT a payment failure (which uses modal now) */}
                                {msg && !msg.includes('Payment') && !msg.includes('Error:') && (
                                    <div style={{ marginBottom: '1.5rem', padding: '1rem', background: '#FFF5F5', color: '#C53030', borderRadius: '6px', border: '1px solid #FED7D7', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <MdError /> {msg}
                                    </div>
                                )}

                                <form onSubmit={handlePreSubmit} className="admission-form">
                                    <div className="form-grid">
                                        <FormInput name="student_name" label="Student Full Name" placeholder="Rahul Sharma" value={formData.student_name} onChange={handleInputChange} />
                                        <FormInput name="email" label="Student Email" type="email" placeholder="student@example.com" value={formData.email} onChange={handleInputChange} required />
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                                            <label style={{ fontSize: '0.85rem', fontWeight: '600', color: '#4A5568' }}>Batch</label>
                                            <select name="batch" value={formData.batch} onChange={handleInputChange} style={{ padding: '0.8rem', borderRadius: '4px', border: '1px solid #CBD5E0', width: '100%', outline: 'none', fontSize: '0.9rem', background: '#FFFFFF' }} required>
                                                <option value="">Select Batch Month</option>
                                                {['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].map(month => (
                                                    <option key={month} value={month}>{month}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                                            <label style={{ fontSize: '0.85rem', fontWeight: '600', color: '#4A5568' }}>Course Name</label>
                                            <select name="course" value={formData.course} onChange={handleInputChange} style={{ padding: '0.8rem', borderRadius: '4px', border: '1px solid #CBD5E0', width: '100%', outline: 'none', fontSize: '0.9rem', background: '#FFFFFF' }} required>
                                                <option value="">Select Course</option>
                                                <option value="KYP">KYP</option>
                                            </select>
                                        </div>

                                        {/* Center Name */}
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', position: 'relative' }}>
                                            <label style={{ fontSize: '0.85rem', fontWeight: '600', color: '#4A5568' }}>Center Name</label>
                                            <div style={{ position: 'relative' }}>
                                                <input
                                                    type="text"
                                                    value={formData.center_name}
                                                    onChange={handleInputChange}
                                                    name="center_name"
                                                    readOnly={user?.role !== 'admin' && user?.role !== 'sub_admin' && !!user?.center_name}
                                                    placeholder="Enter Center Name"
                                                    style={{
                                                        width: '100%', padding: '0.8rem 0.8rem 0.8rem 2.5rem',
                                                        borderRadius: '4px', border: '1px solid #CBD5E0',
                                                        background: (user?.role !== 'admin' && user?.role !== 'sub_admin' && !!user?.center_name) ? '#EDF2F7' : '#FFFFFF',
                                                        color: (user?.role !== 'admin' && user?.role !== 'sub_admin' && !!user?.center_name) ? '#718096' : '#2D3748',
                                                        cursor: (user?.role !== 'admin' && user?.role !== 'sub_admin' && !!user?.center_name) ? 'not-allowed' : 'text',
                                                        fontWeight: 'bold', boxSizing: 'border-box'
                                                    }}
                                                    required
                                                />
                                                <MdClass style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#A0AEC0' }} />
                                            </div>
                                        </div>

                                        <FormInput name="admission_date" label="Admission Date" type="date" value={formData.admission_date} onChange={handleInputChange} />
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                                            <label style={{ fontSize: '0.85rem', fontWeight: '600', color: '#4A5568' }}>Payment Amount (Fixed)</label>
                                            <input name="payment_amount" type="number" value={formData.payment_amount} onChange={handleInputChange} readOnly style={{ padding: '0.8rem', borderRadius: '4px', border: '1px solid #CBD5E0', width: '100%', outline: 'none', fontSize: '0.9rem', background: '#F7FAFC', cursor: 'not-allowed', color: '#718096' }} />
                                        </div>

                                        <FormInput name="drcss_no" label="DRCC No." placeholder="Reg No." value={formData.drcss_no} onChange={handleInputChange} />
                                        <FormInput name="aadhar_no" label="Aadhar Number" placeholder="12 Digit No." value={formData.aadhar_no} onChange={handleInputChange} />
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                                            <label style={{ fontSize: '0.85rem', fontWeight: '600', color: '#4A5568' }}>Batch Time</label>
                                            <select name="batch_time" value={formData.batch_time} onChange={handleInputChange} style={{ padding: '0.8rem', borderRadius: '4px', border: '1px solid #CBD5E0', width: '100%', outline: 'none', fontSize: '0.9rem', background: '#FFFFFF' }} required>
                                                <option value="">Select Time</option>
                                                {['6AM TO 10AM', '7AM TO 11AM', '10AM TO 2PM', '11AM TO 3PM', '2PM TO 6PM', '3PM TO 7PM'].map(time => (
                                                    <option key={time} value={time}>{time}</option>
                                                ))}
                                            </select>
                                        </div>

                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', gridColumn: isMobile ? 'span 1' : 'span 2' }}>
                                            <label style={{ fontSize: '0.85rem', fontWeight: '600', color: '#4A5568' }}>Student Photo <span style={{ color: 'red' }}>*</span></label>

                                            {/* Camera UI Container */}
                                            <div style={{ background: '#F7FAFC', padding: '1rem', borderRadius: '8px', border: '1px dashed #CBD5E0', textAlign: 'center' }}>

                                                {/* Hidden Canvas for Capture */}
                                                <canvas ref={canvasRef} style={{ display: 'none' }} />

                                                {!isCameraOpen && !capturedImage && !cameraError && (
                                                    <div style={{ padding: '1rem' }}>
                                                        <div style={{ marginBottom: '1rem', color: '#718096', fontSize: '0.9rem' }}>Capture live photo using camera</div>
                                                        <button type="button" onClick={startCamera} style={{ background: '#3182CE', color: 'white', border: 'none', padding: '0.6rem 1.2rem', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', margin: '0 auto', fontWeight: 'bold' }}>
                                                            <MdAddCircle /> Open Camera
                                                        </button>
                                                        <div style={{ marginTop: '1rem', fontSize: '0.8rem', color: '#A0AEC0' }}>or</div>
                                                        <button type="button" onClick={() => setCameraError(true)} style={{ background: 'transparent', border: 'none', textDecoration: 'underline', color: '#718096', cursor: 'pointer', fontSize: '0.8rem' }}>
                                                            Issues with camera? Upload File
                                                        </button>
                                                    </div>
                                                )}

                                                {/* Video Preview */}
                                                {isCameraOpen && (
                                                    <div style={{ position: 'relative', width: '100%', maxWidth: '320px', margin: '0 auto' }}>
                                                        <video ref={videoRef} style={{ width: '100%', borderRadius: '8px', background: 'black' }} muted playsInline />

                                                        <button type="button" onClick={switchCamera} style={{ position: 'absolute', top: '10px', right: '10px', background: 'rgba(0,0,0,0.5)', color: 'white', border: 'none', borderRadius: '50%', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 10 }}>
                                                            <MdFlipCameraIos size={20} />
                                                        </button>

                                                        <div style={{ marginTop: '0.8rem', display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                                                            <button type="button" onClick={stopCamera} style={{ background: '#E53E3E', color: 'white', border: 'none', padding: '0.6rem', borderRadius: '4px', cursor: 'pointer' }}>Cancel</button>
                                                            <button type="button" onClick={capturePhoto} style={{ background: '#38A169', color: 'white', border: 'none', padding: '0.6rem 1.5rem', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>Click Photo</button>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Captured Result */}
                                                {capturedImage && (
                                                    <div style={{ textAlign: 'center' }}>
                                                        <img src={capturedImage} alt="Captured" style={{ width: '150px', height: '150px', borderRadius: '50%', objectFit: 'cover', border: '4px solid #3182CE', marginBottom: '1rem' }} />
                                                        <div>
                                                            <button type="button" onClick={retakePhoto} style={{ background: 'white', border: '1px solid #CBD5E0', padding: '0.5rem 1rem', borderRadius: '4px', cursor: 'pointer', color: '#4A5568', fontSize: '0.85rem' }}>Retake Photo</button>
                                                        </div>
                                                        <div style={{ color: '#38A169', fontWeight: 'bold', fontSize: '0.85rem', marginTop: '0.5rem' }}>
                                                            <MdCheckCircle style={{ verticalAlign: 'middle' }} /> Photo Captured
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Fallback File Input */}
                                                {cameraError && (
                                                    <div style={{ textAlign: 'center' }}>
                                                        <div style={{ marginBottom: '0.5rem', color: '#E53E3E', fontSize: '0.85rem' }}>Camera unavailable or permission denied.</div>
                                                        <input
                                                            type="file"
                                                            accept="image/*"
                                                            onChange={(e) => {
                                                                if (e.target.files[0]) {
                                                                    setFormData({ ...formData, photo: e.target.files[0] });
                                                                    setCapturedImage(URL.createObjectURL(e.target.files[0]));
                                                                }
                                                            }}
                                                            style={{ padding: '0.6rem', border: '1px solid #CBD5E0', borderRadius: '4px', width: '100%' }}
                                                            required={!formData.photo} // Required if no photo set
                                                        />
                                                        {formData.photo && (
                                                            <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: '#38A169' }}>
                                                                Selected: {formData.photo.name}
                                                            </div>
                                                        )}
                                                        <button type="button" onClick={() => setCameraError(false)} style={{ marginTop: '0.8rem', background: 'transparent', border: 'none', textDecoration: 'underline', color: '#3182CE', cursor: 'pointer', fontSize: '0.8rem' }}>
                                                            Try Camera Again
                                                        </button>
                                                    </div>
                                                )}

                                                {/* Validation Message if logic fails check */}
                                                <input tabIndex={-1} autoComplete="off" style={{ opacity: 0, height: 0, width: 0, position: 'absolute' }} required={!formData.photo} onInvalid={e => e.target.setCustomValidity('Please capture or upload a student photo')} onInput={e => e.target.setCustomValidity('')} />
                                            </div>
                                        </div>
                                    </div>

                                    <div style={{ marginTop: '1.5rem' }}>
                                        <label style={{ fontSize: '0.85rem', fontWeight: '600', color: '#4A5568', marginBottom: '0.4rem', display: 'block' }}>Permanent Address</label>
                                        <textarea
                                            name="center_address"
                                            placeholder="Complete Address..."
                                            value={formData.center_address}
                                            onChange={handleInputChange}
                                            style={{ width: '100%', padding: '0.8rem', borderRadius: '4px', border: '1px solid #CBD5E0', minHeight: '80px', fontFamily: 'inherit', fontSize: '0.9rem', background: user?.center_address ? '#F7FAFC' : 'white' }}
                                            required
                                            readOnly={!!user?.center_address}
                                        />
                                    </div>

                                    <div style={{ marginTop: '2rem', textAlign: isMobile ? 'center' : 'right' }}>
                                        <button type="submit" disabled={loading} style={{
                                            padding: '0.9rem 3rem',
                                            background: '#319795',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '6px',
                                            fontWeight: '600',
                                            fontSize: '0.95rem',
                                            cursor: 'pointer',
                                            boxShadow: '0 4px 6px rgba(49, 151, 149, 0.4)',
                                            width: isMobile ? '100%' : 'auto'
                                        }}>
                                            {loading ? 'Processing...' : 'Proceed to Payment'}
                                        </button>
                                    </div>
                                </form>
                                <style>{`
                                .form-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1.5rem; }
                                @media (max-width: 1024px) { .form-grid { grid-template-columns: repeat(2, 1fr); } }
                                @media (max-width: 600px) { .form-grid { grid-template-columns: 1fr; } }
                           `}</style>
                            </div>
                        </motion.div>
                    )}

                    {activeTab === 'center_facilities' && (
                        <div className="fade-in">
                            <CenterFacilities />
                        </div>
                    )}

                    {activeTab === 'daily_progress' && (
                        <div className="fade-in h-full">
                            <CenterTargetManagement />
                        </div>
                    )}
                </main>
            </div >

            {/* Custom Confirm Popup */}
            {
                confirmModal.show && (
                    <div style={{
                        position: 'fixed', inset: 0, zIndex: 9999,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: 'rgba(0,0,0,0.45)', padding: '1rem'
                    }}>
                        <div style={{
                            background: 'white', borderRadius: '8px',
                            padding: '1.4rem 1.2rem', width: '100%', maxWidth: '320px',
                            boxShadow: '0 20px 40px rgba(0,0,0,0.25)'
                        }}>
                            <div style={{ fontSize: '1.3rem', marginBottom: '0.6rem' }}>❓</div>
                            <p style={{ margin: '0 0 1.2rem', fontWeight: '600', color: '#1A202C', fontSize: '0.92rem', lineHeight: 1.5 }}>
                                {confirmModal.message}
                            </p>
                            <div style={{ display: 'flex', gap: '0.6rem' }}>
                                <button onClick={handleConfirmYes} style={{
                                    flex: 1, padding: '0.55rem', background: '#38A169',
                                    color: 'white', border: 'none', borderRadius: '4px',
                                    fontWeight: '700', fontSize: '0.9rem', cursor: 'pointer'
                                }}>✓ Haan</button>
                                <button onClick={handleConfirmNo} style={{
                                    flex: 1, padding: '0.55rem', background: '#EDF2F7',
                                    color: '#4A5568', border: 'none', borderRadius: '4px',
                                    fontWeight: '700', fontSize: '0.9rem', cursor: 'pointer'
                                }}>✗ Nahi</button>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Success Modal */}
            <AnimatePresence>
                {showSuccessModal && (
                    <SuccessModal data={successData} onClose={() => { setShowSuccessModal(false); setActiveTab('dashboard'); }} />
                )}
            </AnimatePresence>

            {/* Error Modal */}
            <AnimatePresence>
                {showErrorModal && (
                    <div style={{ position: 'fixed', inset: 0, zIndex: 110, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}>
                        <motion.div
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.8, opacity: 0 }}
                            style={{ background: 'white', padding: '2.5rem', borderRadius: '12px', width: '90%', maxWidth: '400px', textAlign: 'center', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)' }}
                        >
                            <div style={{ width: '60px', height: '60px', background: '#FED7D7', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem auto' }}>
                                <MdError size={36} color="#C53030" />
                            </div>
                            <h2 style={{ fontSize: '1.5rem', fontWeight: '800', color: '#1A202C', marginBottom: '0.5rem' }}>Submission Failed</h2>
                            <p style={{ color: '#E53E3E', fontSize: '1rem', fontWeight: 'bold' }}>{errorData?.message || 'Something went wrong'}</p>
                            <p style={{ color: '#718096', fontSize: '0.9rem', marginBottom: '2rem', marginTop: '0.5rem' }}>
                                {errorData?.details || 'Please try again later. If the amount was deducted, it will be refunded automatically.'}
                            </p>

                            <button onClick={() => { setShowErrorModal(false); setActiveTab('admission'); window.history.replaceState({}, document.title, window.location.pathname); }} style={{ width: '100%', padding: '1rem', background: '#E53E3E', color: 'white', borderRadius: '8px', border: 'none', fontWeight: '700', fontSize: '1rem', cursor: 'pointer' }}>
                                Try Again
                            </button>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* Center Profile Modal */}
            <AnimatePresence>
                {showCenterProfile && (
                    <div style={{ position: 'fixed', inset: 0, zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(5px)' }} onClick={() => setShowCenterProfile(false)}>
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} onClick={(e) => e.stopPropagation()}
                            style={{ background: 'white', padding: '2.5rem', borderRadius: '16px', width: '90%', maxWidth: '400px', textAlign: 'center', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}
                        >
                            <div style={{ marginBottom: '1.5rem', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                <div style={{ width: '100px', height: '100px', borderRadius: '50%', border: '4px solid #E2E8F0', overflow: 'hidden', marginBottom: '1rem', background: 'white', padding: '5px' }}>
                                    <img src={user?.center_logo_url || logo} alt="Center Logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                                </div>
                                <h2 style={{ fontSize: '1.4rem', fontWeight: '800', color: '#1A202C', margin: 0 }}>{user?.center_name || 'My Center'}</h2>
                                <span style={{ color: '#718096', fontSize: '0.9rem', marginTop: '5px' }}>{user?.designation || 'Faculty'}</span>
                            </div>

                            <div style={{ textAlign: 'left', marginBottom: '1.5rem', padding: '0 0.5rem' }}>
                                <div style={{ marginBottom: '1rem', borderBottom: '1px dashed #E2E8F0', paddingBottom: '1rem' }}>
                                    <label style={{ fontSize: '0.7rem', fontWeight: 'bold', color: '#A0AEC0', textTransform: 'uppercase', letterSpacing: '1px' }}>Faculty Name</label>
                                    <div style={{ color: '#2D3748', fontWeight: '700', fontSize: '1.1rem' }}>{user?.name}</div>
                                </div>
                                <div>
                                    <label style={{ fontSize: '0.7rem', fontWeight: 'bold', color: '#A0AEC0', textTransform: 'uppercase', letterSpacing: '1px' }}>Username / Email</label>
                                    <div style={{ color: '#2D3748', fontWeight: '600', fontSize: '1rem' }}>{user?.email}</div>
                                </div>
                            </div>

                            <div style={{ background: '#F7FAFC', padding: '1.2rem', borderRadius: '12px', border: '1px solid #EDF2F7', textAlign: 'left', marginBottom: '2rem' }}>
                                <label style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#A0AEC0', textTransform: 'uppercase', letterSpacing: '1px' }}>Center Address</label>
                                <p style={{ color: '#4A5568', fontSize: '0.95rem', margin: '5px 0 0 0', lineHeight: 1.5 }}>
                                    {user?.center_address || 'No address provided.'}
                                </p>
                            </div>

                            <button onClick={() => setShowCenterProfile(false)} style={{ width: '100%', padding: '0.8rem', background: '#319795', color: 'white', fontWeight: 'bold', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>Close Profile</button>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* Student Details Modal (Read Only) */}
            <AnimatePresence>
                {selectedStudent && (
                    <div style={{
                        position: 'fixed', inset: 0, zIndex: 110,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(5px)',
                        padding: isMobile ? '0' : '1rem'
                    }} onClick={() => setSelectedStudent(null)}>
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0, y: 20 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            onClick={(e) => e.stopPropagation()}
                            className="no-scrollbar"
                            style={{
                                background: '#fff',
                                borderRadius: isMobile ? '0' : '16px',
                                width: '100%',
                                maxWidth: '900px',
                                height: isMobile ? '100%' : 'auto',
                                maxHeight: isMobile ? '100vh' : '95vh',
                                overflowY: 'auto',
                                display: 'flex',
                                flexDirection: 'column',
                                boxShadow: '0 20px 50px rgba(0,0,0,0.3)',
                                paddingTop: 0,
                                paddingRight: isMobile ? '1rem' : '2rem',
                                paddingBottom: isMobile ? '1.5rem' : '2rem',
                                paddingLeft: isMobile ? '1rem' : '2rem',
                            }}
                        >
                            <div style={{ height: '140px', background: 'linear-gradient(135deg, #1A202C 0%, #2D3748 100%)', position: 'relative', flexShrink: 0 }}>
                                <button onClick={() => setSelectedStudent(null)} style={{ position: 'absolute', top: '15px', right: '15px', background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '50%', width: '36px', height: '36px', color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' }}><MdClose size={22} /></button>
                            </div>

                            <div style={{ padding: isMobile ? '0 1rem 1.5rem' : '0 2rem 2rem', position: 'relative', zIndex: 10, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>

                                {/* Profile Image - Centered and Overlapping */}
                                <div style={{
                                    marginTop: '-70px',
                                    width: isMobile ? '110px' : '140px',
                                    height: isMobile ? '110px' : '140px',
                                    borderRadius: '50%',
                                    border: '5px solid white',
                                    background: '#EDF2F7',
                                    overflow: 'hidden',
                                    boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                                }}>
                                    {selectedStudent.photo_path ? (
                                        <img src={selectedStudent.photo_url} alt="Student" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                    ) : (
                                        <div style={{ fontSize: '3rem', fontWeight: 'bold', color: '#CBD5E0' }}>{selectedStudent.student_name.charAt(0)}</div>
                                    )}
                                </div>

                                {/* Name and Tags - Centered Below Image */}
                                <div style={{ textAlign: 'center', marginTop: '1rem', width: '100%' }}>
                                    <h2 style={{
                                        fontSize: isMobile ? '1.5rem' : '2rem',
                                        fontWeight: '800',
                                        color: '#2D3748',
                                        margin: '0 0 0.5rem 0',
                                        letterSpacing: '-0.5px'
                                    }}>
                                        {selectedStudent.student_name}
                                    </h2>

                                    <div style={{ display: 'flex', gap: '0.8rem', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap' }}>
                                        <span style={{ fontSize: '0.8rem', color: '#4A5568', background: '#EDF2F7', padding: '4px 12px', borderRadius: '20px', fontWeight: '600', border: '1px solid #E2E8F0' }}>
                                            Reg: {selectedStudent.registration_no || 'Pending'}
                                        </span>
                                        {selectedStudent.payment_status === 'paid' ? (
                                            <span style={{ fontSize: '0.8rem', color: '#276749', background: '#C6F6D5', padding: '4px 12px', borderRadius: '20px', fontWeight: '600', border: '1px solid #9AE6B4' }}>
                                                Active Student
                                            </span>
                                        ) : (
                                            <span style={{ fontSize: '0.8rem', color: '#744210', background: '#FEEBC8', padding: '4px 12px', borderRadius: '20px', fontWeight: '600', border: '1px solid #FBD38D' }}>
                                                Payment Pending
                                            </span>
                                        )}
                                    </div>

                                    {/* Action Button from Modal if Pending */}
                                    {selectedStudent.payment_status !== 'paid' && (
                                        <div style={{ marginTop: '1rem' }}>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    verifyPayment(selectedStudent.order_id);
                                                    setSelectedStudent(null);
                                                }}
                                                style={{ background: '#3182CE', color: 'white', border: 'none', padding: '0.6rem 1.2rem', borderRadius: '6px', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 'bold', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
                                            >
                                                <MdRestore /> Check Payment Status
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {/* Info Grid */}
                                <div style={{ width: '100%', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem', marginTop: '2rem' }}>

                                    {/* Academic Info */}
                                    <div style={{ background: 'white', padding: '1.5rem', borderRadius: '12px', border: '1px solid #E2E8F0', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                                        <h4 style={{ fontSize: '0.8rem', color: '#553C9A', textTransform: 'uppercase', marginBottom: '1.2rem', fontWeight: '700', borderBottom: '2px solid #E9D8FD', paddingBottom: '0.5rem', display: 'inline-block' }}>
                                            Academic Details
                                        </h4>
                                        <div style={{ display: 'grid', gap: '1rem' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px dashed #EDF2F7', paddingBottom: '0.5rem' }}>
                                                <span style={{ fontSize: '0.85rem', color: '#718096' }}>Course</span>
                                                <span style={{ fontWeight: '600', color: '#2D3748' }}>{selectedStudent.course}</span>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px dashed #EDF2F7', paddingBottom: '0.5rem' }}>
                                                <span style={{ fontSize: '0.85rem', color: '#718096' }}>Batch</span>
                                                <span style={{ fontWeight: '600', color: '#2D3748' }}>{selectedStudent.batch}</span>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <span style={{ fontSize: '0.85rem', color: '#718096' }}>Time</span>
                                                <span style={{ fontWeight: '600', color: '#2D3748' }}>{selectedStudent.batch_time}</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Personal Info */}
                                    <div style={{ background: 'white', padding: '1.5rem', borderRadius: '12px', border: '1px solid #E2E8F0', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                                        <h4 style={{ fontSize: '0.8rem', color: '#2C7A7B', textTransform: 'uppercase', marginBottom: '1.2rem', fontWeight: '700', borderBottom: '2px solid #B2F5EA', paddingBottom: '0.5rem', display: 'inline-block' }}>
                                            Personal Info
                                        </h4>
                                        <div style={{ display: 'grid', gap: '1rem' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px dashed #EDF2F7', paddingBottom: '0.5rem' }}>
                                                <span style={{ fontSize: '0.85rem', color: '#718096' }}>Aadhar</span>
                                                <span style={{ fontWeight: '600', color: '#2D3748' }}>{selectedStudent.aadhar_no}</span>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px dashed #EDF2F7', paddingBottom: '0.5rem' }}>
                                                <span style={{ fontSize: '0.85rem', color: '#718096' }}>DRCC</span>
                                                <span style={{ fontWeight: '600', color: '#2D3748' }}>{selectedStudent.drcss_no}</span>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <span style={{ fontSize: '0.85rem', color: '#718096' }}>Admission Date</span>
                                                <span style={{ fontWeight: '600', color: '#2D3748' }}>{selectedStudent.admission_date}</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Address Info */}
                                    <div style={{ background: 'white', padding: '1.5rem', borderRadius: '12px', border: '1px solid #E2E8F0', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                                        <h4 style={{ fontSize: '0.8rem', color: '#C05621', textTransform: 'uppercase', marginBottom: '1.2rem', fontWeight: '700', borderBottom: '2px solid #FEEBC8', paddingBottom: '0.5rem', display: 'inline-block' }}>
                                            Contact Address
                                        </h4>
                                        <div>
                                            <p style={{ lineHeight: 1.6, color: '#4A5568', fontSize: '0.95rem', margin: 0 }}>
                                                {selectedStudent.center_address}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* Manual Payment Modal */}
            <AnimatePresence>
                {showManualPaymentModal && manualPaymentData && (
                    <motion.div
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        onClick={() => { if (!manualPaymentData?.forced) setShowManualPaymentModal(false); }}
                        style={{
                            position: 'fixed', inset: 0, zIndex: 60,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(5px)',
                            cursor: manualPaymentData?.forced ? 'not-allowed' : 'default'
                        }}
                    >
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
                            onClick={(e) => e.stopPropagation()}
                            style={{
                                background: 'white', width: '90%', maxWidth: '420px',
                                borderRadius: '16px', overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                                maxHeight: '90vh', overflowY: 'auto'
                            }}
                        >
                            <div style={{ background: '#2C5282', padding: '1rem', textAlign: 'center', color: 'white' }}>
                                <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 'bold' }}>Complete Payment</h3>
                                <div style={{ fontSize: '2rem', fontWeight: '800', marginTop: '0.2rem' }}>₹{manualPaymentData.amount}</div>
                                <div style={{ fontSize: '0.8rem', opacity: 0.8 }}>admission fee for {manualPaymentData.student_name}</div>
                            </div>

                            <div style={{ padding: '1.2rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>

                                {/* QR Code Section */}
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{
                                        width: '160px', height: '160px', margin: '0 auto 0.5rem',
                                        border: '2px solid #E2E8F0', borderRadius: '12px', padding: '8px',
                                        background: 'white'
                                    }}>
                                        <img src="/qr-code.jpeg" alt="Payment QR" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                                    </div>
                                    <div style={{ fontSize: '0.85rem', color: '#4A5568', fontWeight: '600' }}>Scan to Pay via Any UPI App</div>
                                    <div style={{
                                        background: '#EBF8FF', color: '#2C5282', padding: '0.4rem 0.8rem',
                                        borderRadius: '6px', fontSize: '0.85rem', fontWeight: 'bold',
                                        marginTop: '0.4rem', display: 'inline-block', border: '1px dashed #4299E1'
                                    }}>
                                        scsmdehri@ybl
                                    </div>
                                </div>

                                {/* Mobile Actions */}
                                {isMobile && (
                                    <div style={{ width: '100%', display: 'flex', gap: '0.5rem', flexDirection: 'column' }}>
                                        <a href={`upi://pay?pa=scsmdehri@ybl&pn=SCSM%20Computer&am=${manualPaymentData.amount}&cu=INR`}
                                            style={{
                                                textDecoration: 'none', background: '#5f259f', color: 'white', padding: '0.6rem',
                                                borderRadius: '8px', textAlign: 'center', fontWeight: 'bold', display: 'flex',
                                                alignItems: 'center', justifyContent: 'center', gap: '0.5rem', fontSize: '0.9rem'
                                            }}>
                                            <SiPhonepe size={18} /> Pay with PhonePe
                                        </a>
                                        <a href={`upi://pay?pa=scsmdehri@ybl&pn=SCSM%20Computer&am=${manualPaymentData.amount}&cu=INR`}
                                            style={{
                                                textDecoration: 'none', background: '#1a73e8', color: 'white', padding: '0.6rem',
                                                borderRadius: '8px', textAlign: 'center', fontWeight: 'bold', display: 'flex',
                                                alignItems: 'center', justifyContent: 'center', gap: '0.5rem', fontSize: '0.9rem'
                                            }}>
                                            <SiGooglepay size={18} /> Pay with Google Pay
                                        </a>
                                    </div>
                                )}

                                {/* UTR Input Section */}
                                <div style={{ width: '100%', textAlign: 'left' }}>
                                    <label style={{ fontSize: '0.85rem', fontWeight: 'bold', color: '#2D3748', display: 'block', marginBottom: '0.4rem' }}>
                                        Transaction ID / UTR Number <span style={{ color: 'red' }}>*</span>
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="Enter 12-digit UTR from your UPI App"
                                        value={utrNumber || ''}
                                        onChange={(e) => setUtrNumber(e.target.value)}
                                        style={{
                                            width: '100%', padding: '0.8rem', borderRadius: '8px', border: '1px solid #CBD5E0',
                                            fontSize: '0.9rem', outline: 'none', background: '#F7FAFC'
                                        }}
                                    />
                                    <div style={{ fontSize: '0.75rem', color: '#718096', marginTop: '0.3rem' }}>* Mandatory for payment verification</div>
                                </div>

                                <div style={{ width: '100%', borderTop: '1px solid #E2E8F0', paddingTop: '1rem' }}>
                                    <button
                                        disabled={!utrNumber || utrNumber.length < 10}
                                        onClick={async () => {
                                            try {
                                                setLoading(true);
                                                await api.post('/admissions/submit-utr', {
                                                    admission_id: manualPaymentData.admission_id,
                                                    utr_number: utrNumber
                                                });

                                                setShowManualPaymentModal(false);
                                                setUtrNumber(''); // Reset
                                                setSuccessData({ registration_no: 'Pending Approval' }); // Temporary fake data for modal
                                                // Customize Success Modal for Manual
                                                alert("Payment Submitted! Admission is PENDING Admin Approval. Once approved, Registration No will be generated.");

                                                setFormData(prev => ({
                                                    student_name: '', email: '', batch: '', batch_time: '', drcss_no: '', aadhar_no: '',
                                                    course: '', admission_date: '', payment_amount: '',
                                                    center_name: user?.center_name || '',
                                                    center_address: user?.center_address || '',
                                                    photo: null
                                                }));
                                                setCapturedImage(null);
                                                setConductedBy('');
                                                fetchDashboardData();
                                            } catch (err) {
                                                alert("Failed to submit UTR: " + (err.response?.data?.message || err.message));
                                            } finally {
                                                setLoading(false);
                                            }
                                        }}
                                        style={{
                                            width: '100%', padding: '0.8rem',
                                            background: (!utrNumber || utrNumber.length < 10) ? '#CBD5E0' : '#2F855A',
                                            color: 'white',
                                            border: 'none', borderRadius: '8px', fontWeight: 'bold', fontSize: '0.95rem',
                                            cursor: (!utrNumber || utrNumber.length < 10) ? 'not-allowed' : 'pointer',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
                                            transition: 'background 0.2s'
                                        }}
                                    >
                                        <MdFileDownloadDone size={20} /> I have made the payment
                                    </button>
                                    {manualPaymentData?.forced ? (
                                        // FORCED MODE: Cannot cancel - must complete UTR
                                        <div style={{
                                            marginTop: '0.8rem', padding: '0.8rem',
                                            background: '#FFF5F5', border: '1px solid #FEB2B2',
                                            borderRadius: '8px', textAlign: 'center'
                                        }}>
                                            <div style={{ fontSize: '0.8rem', color: '#C53030', fontWeight: '700' }}>
                                                ⚠️ पिछला Admission अधूरा है!
                                            </div>
                                            <div style={{ fontSize: '0.75rem', color: '#742A2A', marginTop: '0.3rem' }}>
                                                UTR Number दर्ज करें और "I have made the payment" दबाएं।
                                                जब तक यह पूरा नहीं होगा, आगे कुछ भी नहीं होगा।
                                            </div>
                                        </div>
                                    ) : (
                                        <button
                                            onClick={() => setShowManualPaymentModal(false)}
                                            style={{
                                                width: '100%', marginTop: '0.5rem', padding: '0.6rem', background: 'transparent',
                                                color: '#718096', border: 'none', fontWeight: '600', cursor: 'pointer', fontSize: '0.85rem'
                                            }}
                                        >
                                            Cancel / Pay Later
                                        </button>
                                    )}
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>


            <style>{`
                .no-scrollbar::-webkit-scrollbar { display: none; }
                .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
                
                .placeholder-white::placeholder { color: rgba(255,255,255,0.8); }
                .placeholder-white { color: white; }

                @media (max-width: 900px) {
                    .mobile-overlay { display: block !important; }
                    .main-content { margin-left: 0 !important; }
                    .mobile-only-close { display: block !important; }
                }
            `}</style>
        </div >
    );
}

// Utility Components
function NavItem({ icon, label, isActive, onClick, badge }) {
    return (
        <div onClick={onClick} style={{
            display: 'flex', alignItems: 'center', gap: '0.8rem', padding: '0.5rem 0.8rem', margin: '0.1rem 0.5rem',
            background: isActive ? 'linear-gradient(90deg, #319795 0%, #2C7A7B 100%)' : 'transparent',
            color: isActive ? 'white' : '#94a3b8',
            borderRadius: '6px', cursor: 'pointer', transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            fontWeight: isActive ? '700' : '500', fontSize: '0.8rem',
            whiteSpace: 'nowrap',
            boxShadow: isActive ? '0 4px 6px -1px rgba(0, 0, 0, 0.1)' : 'none'
        }}>
            <div style={{ fontSize: '1.2rem', display: 'flex', flexShrink: 0, opacity: isActive ? 1 : 0.7 }}>{icon}</div>
            <span style={{ flex: 1 }}>{label}</span>
            {badge && (
                <span style={{ background: '#E53E3E', color: 'white', fontSize: '0.6rem', fontWeight: '800', padding: '1px 5px', borderRadius: '10px', letterSpacing: '0.3px' }}>
                    {badge}
                </span>
            )}
        </div>
    )
}

function StatCard({ label, value, icon, bg }) {
    return (
        <div style={{ background: bg, padding: '1.5rem', borderRadius: '12px', color: 'white', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '120px', width: '250px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '0.85rem', fontWeight: '600', opacity: 0.9 }}>{label}</span>
                <div style={{ fontSize: '1.4rem', opacity: 0.8 }}>{icon}</div>
            </div>
            <h3 style={{ fontSize: '2rem', fontWeight: '800', margin: 0 }}>{value}</h3>
        </div>
    )
}

function FormInput({ name, type = "text", placeholder, value, onChange, label, readOnly }) {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
            {label && <label style={{ fontSize: '0.85rem', fontWeight: '600', color: '#4A5568' }}>{label}</label>}
            <input
                name={name}
                type={type}
                placeholder={placeholder}
                value={value || ''}
                onChange={onChange}
                readOnly={readOnly}
                style={{
                    padding: '0.8rem',
                    borderRadius: '4px',
                    border: '1px solid #CBD5E0',
                    width: '100%',
                    boxSizing: 'border-box',
                    outline: 'none',
                    fontSize: '0.9rem',
                    background: readOnly ? '#F7FAFC' : '#FFFFFF',
                    cursor: readOnly ? 'not-allowed' : 'text'
                }}
                required
            />
        </div>
    )
}

function SuccessModal({ data, onClose }) {
    return (
        <div style={{ position: 'fixed', inset: 0, zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}>
            <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                style={{ background: 'white', padding: '2.5rem', borderRadius: '12px', width: '90%', maxWidth: '400px', textAlign: 'center', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)' }}
            >
                <div style={{ width: '60px', height: '60px', background: '#C6F6D5', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem auto' }}>
                    <MdCheckCircle size={36} color="#2F855A" />
                </div>
                <h2 style={{ fontSize: '1.5rem', fontWeight: '800', color: '#1A202C', marginBottom: '0.5rem' }}>Admission Confirmed!</h2>
                <p style={{ color: '#718096', fontSize: '0.9rem', marginBottom: '2rem' }}>Payment was successful and the student record has been generated.</p>

                <div style={{ background: '#F7FAFC', padding: '1.5rem', borderRadius: '8px', marginBottom: '2rem', border: '1px dashed #CBD5E0' }}>
                    <p style={{ fontSize: '0.8rem', color: '#718096', margin: '0 0 0.5rem 0', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: '600' }}>Registration No</p>
                    <div style={{ display: 'flex', alignItems: 'center', justifyItems: 'center', justifyContent: 'center', gap: '8px' }}>
                        <h3 style={{ fontSize: '1.8rem', fontWeight: '800', color: '#319795', margin: 0 }}>{data.registration_no}</h3>
                    </div>
                </div>

                <button onClick={onClose} style={{ width: '100%', padding: '1rem', background: '#319795', color: 'white', borderRadius: '8px', border: 'none', fontWeight: '700', fontSize: '1rem', cursor: 'pointer' }}>
                    Done
                </button>
            </motion.div>
        </div>
    )
}

function Th({ children }) {
    return (
        <th style={{ textAlign: 'left', padding: '1rem', fontSize: '0.8rem', textTransform: 'uppercase', color: '#718096', fontWeight: '700', letterSpacing: '0.5px' }}>
            {children}
        </th>
    )
}

function Td({ children, ...props }) {
    return (
        <td style={{ padding: '1rem', fontSize: '0.9rem', color: '#4A5568' }} {...props}>
            {children}
        </td>
    )
}
